import { logEntry, logEvent } from "@agtech/core/logging/AgTechLogger";
import React, { CSSProperties, useRef } from "react";
import { AgTechGeneralIcon } from "../Icons/AgTechIcons";
import { IAgTechGridItem, AgTechGridColumnMetadata } from "./Data/AgTechGridData";
import { AgTechGridEntity, AgTechGridEntityState, AgTechGridEntityStatus } from "./Data/AgTechGridEntities";
import { AgTechGridConfiguration, AgTechGridEventHandlers } from "./Data/AgTechGridProps";
import { AgTechGridRowStatusInvalidIcon, AgTechGridRowStatusCreatedIcon, AgTechGridRowStatusUpdatedIcon } from "./Rows/AgTechGridRowStatusIcons";
import { AgTechGridToggleControl } from "./Controls/AgTechGridToggleControl";

export type AgTechGridRowProps<TItem extends IAgTechGridItem, TContextData = {}> = {
    index: number,
    entity: AgTechGridEntity<TItem>,
    context: TContextData,
    state: AgTechGridEntityState,
    columns: AgTechGridColumnMetadata<TItem, TContextData>[],
    style: CSSProperties,
    gridConfiguration: AgTechGridConfiguration<TItem>,
    hasRowChanged: (prev: TItem, curr: TItem) => boolean,
    handlers: AgTechGridEventHandlers<TItem>,
    isFocused: boolean
}

const AgTechGridRow = <TItem extends IAgTechGridItem, TContextData = {}>(
    props: AgTechGridRowProps<TItem, TContextData>
) => {
    const gridContext = props.gridConfiguration;
    const gridRowEntity = useRef({
        ...props.entity
    });

    gridRowEntity.current = {
        ...props.entity
    };
    
    // Handles whenever a key is pressed within this grid row
    const onRowKeyDown = async (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            await props.handlers.onEnterKeyPressed(e.key, gridRowEntity.current, props.entity.item);
        }
    }

    // Handles whenever the user attempts to delete this grid row
    const onRowDeleted = async () => {
        await props.handlers.onItemDeleted(gridRowEntity.current);
    }

    const onRowFocused = async () => {
        await props.handlers.onRowFocused(gridRowEntity.current);
    }

    const handleRowSelectionChange = async (wasRowSelected: boolean) => {
        if (wasRowSelected && !props.state.isSelected) {
            await props.handlers.onItemSelected(gridRowEntity.current);
        }
        else if (props.state.isSelected) {
            await props.handlers.onItemDeselected(gridRowEntity.current);
        }
    }

    const isRowSelected = gridContext.selectionConfig && props.state.isSelected;
    const isRowEven = (props.index + 1) % 2 === 0;
    
    const rowClickableClass = gridContext.selectionConfig ? 'clickable' : '';
    const rowFocusClass = props.isFocused && !gridContext.selectionConfig && !gridContext.editConfig ? 'focused' : '';
    const rowSelectedClass = isRowSelected ? '!bg-highlight-50' : '';
    const rowIndexClass = isRowEven ? 'even' : 'odd';
    const rowStatusClass = props.state.status === AgTechGridEntityStatus.Invalid ? 'invalid' : '';
    const rowBorderClass = props.entity.attributes?.isLastItemInGroup ? '' : 'bottom-bordered-table-bordered';

    return (
        <div
            className={`column-based bg-white border-l ${rowClickableClass} ${rowIndexClass} ${rowStatusClass} ${rowFocusClass} ${rowSelectedClass} ${rowBorderClass} last:border-b-0 flex`}
            style={props.style}
            onBlur={e => onGridRowBlur(e, props.entity, gridRowEntity, props.handlers)}
            onKeyDown={onRowKeyDown}
            onClick={onRowFocused}
        >
            { gridContext.selectionConfig ? (
                <div className="tall right-bordered centered flex" style={{ width: 36 }}>
                    <AgTechGridToggleControl
                        value={props.state.isSelected}
                        onUpdated={handleRowSelectionChange}
                    />
                </div>
            )
            : null }

            { gridContext.editConfig && (gridContext.editConfig.showStatus ?? true) ? 
                <div className="centered snug right-bordered flex" style={{ width: 36 }}>
                    <AgTechGridRowStatusIcon entity={gridRowEntity.current} />
                </div>
            : null }
                
            {props.columns.map(column => {                
                let columnSizeStyle: any = column.size ? { flex: column.size } : { flex: 1 };
                            
                if (column.width) {
                    columnSizeStyle = { width: column.width, minWidth: column.width };
                }

                const columnValue = column.value(gridRowEntity.current.item, props.context, updateItem => {
                    const updatedItem = updateItem(gridRowEntity.current.item);

                    gridRowEntity.current = {
                        ...gridRowEntity.current,
                        item: { ...updatedItem }
                    };

                    logEntry('AgTechGrid', 'Grid entity updated - ' + column.header, gridRowEntity.current);
                });

                const columnClasses = column.classes ?? '';
                const columnContentClasses = column.contentClasses ?? '';
                const columnItemClasses = column.itemBasedClasses ? column.itemBasedClasses(gridRowEntity.current.item) : '';

                return (
                    <div
                        key={column.header}
                        className={`row-based text-sm border-r overflow-hidden last:border-none ${columnClasses} flex`}
                        style={columnSizeStyle}
                    >
                        <div className={`stretched overflow-hidden items-center ${columnClasses} ${columnContentClasses} ${columnItemClasses} flex`}>
                            {isGridFormElement(columnValue)
                                ? columnValue :
                                <div className="px-3 font-ellipses">{columnValue}</div>
                            }
                        </div>
                    </div>
                )
            })}

            { gridContext.editConfig?.deleteItem ? 
                <div className="agtech-grid-surface-action-column">
                    <i className="stretched centered fas fa-times clickable color-changing hover:text-highlight flex" onClick={onRowDeleted} />
                </div>
            : null }

            { gridContext.actions?.rendering && (
                <div className="m-0 centered flex" style={{ width: gridContext.actions.width ?? 36 }}>
                    { gridContext.actions.rendering(gridRowEntity.current.item) }
                </div>
            )}

        </div>
    )
}

const onGridRowBlur = async <TItem extends IAgTechGridItem>(
    rowDiv: React.FocusEvent<HTMLDivElement>,
    originalEntity: AgTechGridEntity<TItem>,
    updatedEntity: React.MutableRefObject<AgTechGridEntity<TItem>>,
    handlers: AgTechGridEventHandlers<TItem>,
) => {
    if (!rowDiv.relatedTarget) {
        // Wait to process blur if the blur occurred due to a click outside of the grid row. This could be due to a
        // click in a selection popup and will need time for the popup to process before processing the blur
        setTimeout(async () => {
            await handlers.onRowBlurred(updatedEntity.current, originalEntity.item);
        }, 200);
    }
    else {
        let wasRowBlurred = !rowDiv.currentTarget.contains(rowDiv.relatedTarget as Node);

        if (wasRowBlurred) {
            await handlers.onRowBlurred(updatedEntity.current, originalEntity.item);
        }
    }
}

function isGridFormElement(element: React.ReactNode | JSX.Element): element is JSX.Element {
    return (element as JSX.Element)?.type !== undefined;
}

const AgTechGridRowStatusIcon = <TItem extends IAgTechGridItem>( { entity }: { entity: AgTechGridEntity<TItem> }) => {
    return entity.state.status === AgTechGridEntityStatus.Invalid ? <AgTechGridRowStatusInvalidIcon entity={entity} />
         : entity.state.status === AgTechGridEntityStatus.Created ? <AgTechGridRowStatusCreatedIcon entity={entity} />
         : entity.state.status === AgTechGridEntityStatus.Updated ? <AgTechGridRowStatusUpdatedIcon entity={entity} />
         : <AgTechGeneralIcon />;
}

const AgTechGridRowMemo = React.memo(AgTechGridRow, (prev, curr) => {
    let isSame =
           prev.entity.versionNumber === curr.entity.versionNumber &&
           prev.entity.id === curr.entity.id &&
           prev.entity.state.isSelected === curr.entity.state.isSelected &&
           prev.entity.state.status === curr.entity.state.status &&
           !curr.hasRowChanged(prev.entity.item, curr.entity.item);

    if (!isSame) {
        logEvent('Grid row changed', {
            prevItem: prev.entity,
            currItem: curr.entity,
        });
    }

    return isSame;
}) as
typeof AgTechGridRow;

export default AgTechGridRowMemo;